import React, { useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { DialogContext } from '../App';

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** Tailwind max-width class; the default suits short confirmations. */
  maxWidthClass?: string;
}

export const Dialog: React.FC<DialogProps> = ({ open, onOpenChange, children, maxWidthClass = 'max-w-[450px]' }) => {
  const { setIsDialogOpen } = useContext(DialogContext);

  // Update the global dialog state
  useEffect(() => {
    setIsDialogOpen(open);
    if (open) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      setIsDialogOpen(false);
      document.body.style.overflow = 'unset';
    };
  }, [open, setIsDialogOpen]);

  if (!open) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[999]"
        onClick={() => onOpenChange(false)}
      />

      {/* Dialog box. Keeps a margin on a phone, and scrolls rather than
          running off the top and bottom of a short screen. */}
      <div
        className={`fixed inset-0 m-auto bg-white p-4 sm:p-6 w-[calc(100%-2rem)] ${maxWidthClass} h-fit max-h-[90dvh] overflow-y-auto rounded-xl shadow-xl border border-gray-200 z-[1000] animate-scale-in`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          {children}
        </div>
      </div>
    </>,
    document.body
  );
};

export const DialogTrigger: React.FC<{ asChild?: boolean; children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

export const DialogHeader: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`mb-4 ${className}`}>{children}</div>
);

export const DialogTitle: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <h2 className={`text-xl font-semibold text-navy ${className}`}>{children}</h2>
);

export const DialogDescription: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <p className={`text-gray-600 text-sm mt-2 ${className}`}>{children}</p>
);

export const DialogFooter: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`mt-6 flex justify-end gap-3 ${className}`}>{children}</div>
);

export const DialogContent: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => {
  return <div className={`space-y-4 text-gray-700 ${className}`}>{children}</div>;
};
